import type {
  DeepPartial,
  DeepReadonly,
  CreateItem,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useCallback } from "react"

import { useUpsertCollectionItem } from "src/hooks/rxdb"
import type { CollectionKey } from "src/types"
import { noticeError } from "src/utils"

// pass the ReturnT argument if the return type is different than the argument type
export function useCreateCollectionItem<
  Key extends string,
  Item,
  CreateItemPayload = Item,
  TransformedItem = Item,
>(
  collectionKey: CollectionKey,
  responseKey: Key,
  useCreateItem: () => CreateItem<Item, Key, CreateItemPayload> | undefined,
  transform: (i: Item) => TransformedItem,
) {
  const createItem = useCreateItem()
  const upsertCollectionItem = useUpsertCollectionItem<TransformedItem>(collectionKey)

  return useCallback(
    async (newItem: DeepReadonly<DeepPartial<CreateItemPayload>>) => {
      try {
        if (!createItem) {
          throw new Error(`cannot create ${responseKey} for unauthorized user`)
        }

        const response = await createItem(newItem)
        const item = response.data[responseKey]
        await upsertCollectionItem(transform(item))
        return response
      } catch (e) {
        noticeError(
          e,
          "CreateCollectionItem",
          {
            [responseKey]: newItem,
            collectionItem: responseKey,
            collectionKey,
          },
        )

        throw e
      }
    },
    [
      collectionKey,
      responseKey,
      createItem,
      transform,
      upsertCollectionItem,
    ],
  )
}
